import {container} from "tsyringe";

import type {Employee} from "../entities/employee.entity";
import {formatPersonName} from "../helpers/format-person-name.helper";
import {EmployeeRepository} from "../repositories/employee.repository";

/** One employee as the picker gimmick draws them (feature plan 0072). */
export interface PoolEntry {
    readonly uid: string;
    /** "Vorname Nachname"; "?" when the employee has neither. */
    readonly name: string;
    /** For the wheel's segments and the drum's balls (`Employee.shortcut` falls back to the initials); "?" when
     *  empty or blank (the getter gives " " for a first name of blanks). */
    readonly shortcut: string;
    /** The employee's colour, raw — how it is shown decides `components/gimmicks/picker/pool-color.ts`. */
    readonly color: string;
    /** The employee's text colour (feature plan 0074): null or absent = automatic against `color`. */
    readonly textColor?: string | null;
}

/** All active (not deleted) employees — the preloaded cache — sorted by name ('de'), then uid: a stable order
 *  on wheel and reel. */
export async function loadEmployeePool(): Promise<PoolEntry[]> {
    const employees = await container.resolve(EmployeeRepository).findAll([], -1, 0, true);
    return (Array.from(employees) as Employee[])
        .map((employee): PoolEntry => ({
            uid: employee.uid,
            name: formatPersonName(employee.lastName, employee.firstName, 'natural') || '?',
            shortcut: employee.shortcut.trim() || '?',
            color: employee.color ?? '',
            textColor: employee.textColor,
        }))
        .sort((a, b) => a.name.localeCompare(b.name, 'de') || (a.uid < b.uid ? -1 : a.uid > b.uid ? 1 : 0));
}
